import { GAMING_DAY_TIMEZONE } from '@dayon/contracts';

/**
 * BRANDS & LICENCES — the wire shapes, and the small facts every part of the
 * screen derives from them.
 *
 * snake_case, straight off the SELECT list in
 * `services/api/src/modules/backoffice/platform.ts` (`GET /api/platform/brands`).
 * Nothing is renamed on the way in: a field that reads differently here than in
 * the SQL is a field somebody will eventually mis-map.
 *
 * The WRITE routes are camelCase, because they are Zod contracts rather than
 * rows — `POST /api/platform/brands` and `PATCH /api/platform/brands/:id` in
 * `services/api/src/modules/platform-admin/brands.ts`.
 */

export interface BrandRow {
  id: string;
  ref: string;
  name: string;
  domain: string;
  licence_ref: string | null;
  licence_expires_at: string | null;
  active: boolean;
  /** Counts and booleans are cast ::text in SQL to survive the pg driver. */
  players: string;
  /** `licence_expires_at < now() + interval '60 days'`, computed server-side. */
  licence_expiring: string;
  /** `licence_expires_at <= now()`, computed server-side. See `licenceState`. */
  licence_expired: string;
}

/** What a write returns. `affectedPlayers` is present only on a deactivation. */
export interface BrandWriteResult {
  id: string;
  ref: string;
  name: string;
  domain: string;
  licenceRef: string | null;
  licenceExpiresAt: string | null;
  active: boolean;
  affectedPlayers?: number;
}

/**
 * Dates on this screen are rendered on the gaming day's own clock
 * (`GAMING_DAY_TIMEZONE` from @dayon/contracts) and nothing else.
 *
 * NOT `toLocaleDateString('en-PH')`. That sets the FORMAT and says nothing about
 * the ZONE, so it renders in the viewer's browser timezone — ten screens in
 * this console did exactly that and read as correct because the locale is
 * Philippine. Between 16:00 and 24:00 UTC, which is midnight to 08:00 in
 * Manila, an operator in London and an operator in Manila saw different dates
 * over the same figures.
 *
 * DAY FIRST, MONTH IN WORDS: "09 Oct 2027", as Platform settings writes the same
 * licence date. This screen wrote "10/9/2027", which reads as 10 September to
 * anyone who writes the day first (module walk, 30 Oct 2026).
 */
const licenceDay = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: GAMING_DAY_TIMEZONE,
});

export const day = (iso: string | null): string => (iso ? licenceDay.format(new Date(iso)) : '—');

/**
 * Whole days from now until an instant, negative once it has passed.
 *
 * A licence expires at a REGULATOR'S INSTANT, not on a gaming day — which is
 * why this is a difference of instants and not a difference of gaming dates.
 * The same reasoning is written out at `licenceWarnings()` in
 * `services/api/src/modules/dashboard/queries.ts`.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THIS IS A COUNTDOWN FOR THE EYE, AND IT DECIDES NOTHING.
 *
 * It rounds to whole days and it reads `Date.now()`, which is the operator's
 * OWN CLOCK. Both are fine for printing "in 34 days" beside a date and neither
 * is fit to decide whether a licence has lapsed:
 *
 *   · `Math.round` maps everything inside ±12 hours of the expiry to 0, so a
 *     licence that ran out this morning is not negative here. `licenceState`
 *     used to test `daysUntil(...) < 0` and therefore called it in date for up
 *     to twelve hours after the server had already called it expired — and
 *     `Math.round(-0.4)` is `-0`, which is not `< 0` either.
 *   · a machine whose clock is wrong moves a COMPLIANCE STATE on screen, and
 *     nothing on the screen would say so.
 *
 * Whether a licence has lapsed is `b.licence_expired`, answered by the server
 * against the database's own `now()`. See `licenceState` below.
 * ══════════════════════════════════════════════════════════════════════════
 */
export const daysUntil = (iso: string): number =>
  Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);

export type LicenceState = 'NONE' | 'EXPIRED' | 'EXPIRING' | 'IN_DATE';

/**
 * The four states a licence can actually be in, derived from what the API sent
 * and nothing else — which is now true of all four rather than of three.
 *
 * BOTH FLAGS COME FROM THE SERVER, and for the same reason. The dashboard
 * warning, the settings screen and this screen have to agree about what
 * "inside the window" and "lapsed" mean, and a second copy of either
 * calculation in the browser is a second chance to disagree:
 *
 *   · `licence_expiring` is `licence_expires_at < now() + interval '60 days'`;
 *   · `licence_expired` is `licence_expires_at <= now()`, byte-for-byte the
 *     predicate `licenceWarnings()` uses for the dashboard's own `expired`.
 *
 * EXPIRED was the one that was NOT. It was `daysUntil(...) < 0` — whole days,
 * rounded, off the browser's clock — so for about twelve hours after a licence
 * ran out this screen said "In date" while the dashboard said it had lapsed.
 * That is the header comment's claim finally made true rather than restated.
 *
 * Order matters: a lapsed licence is also inside the 60-day window, so EXPIRED
 * is tested first.
 */
export function licenceState(b: BrandRow): LicenceState {
  if (!b.licence_expires_at) return 'NONE';
  if (b.licence_expired === 'true') return 'EXPIRED';
  return b.licence_expiring === 'true' ? 'EXPIRING' : 'IN_DATE';
}

export const LICENCE_LABEL: Record<LicenceState, string> = {
  NONE: 'No expiry recorded',
  EXPIRED: 'Lapsed',
  EXPIRING: 'Expiring within 60 days',
  IN_DATE: 'In date',
};
